/**
 * Data quality checks of the Strategic KPIs page (masterplan skpi-redesign E13, packages D1 / D1a) - pure functions over the
 * engine's response. Two checks exist, both from data the page already has:
 *
 *   mapping  meetings without a company mapping (`dataQuality.meetingsWithoutCompanyMapping`, the engine's count)
 *   targets  persons without a target: active, not excluded, in a BU that has targets, with no target on a KPI that
 *            carries person targets
 *
 * "Not excluded" needs no filter here: the engine removes persons flagged KpiPersonYear.IsExcluded before it builds
 * `persons`. No KPI figure is computed - only whether a stored target is present.
 *
 * Rules of the targets check (D1a), independent of `steeredKpis(top)` and of the selection:
 *   1. A person "has a target" = `values[k].target` is set on any KPI that takes person targets (grain PERSON, not derived).
 *      Ignored targets are already null in the response.
 *   2. A BU "has targets" = the payload shows one for it: an ACTIVE person with a target (rule 1), or a whole group node
 *      (BU, company, team) with its own stored target. Persons who left do not count.
 *   3. With an active selection the payload only holds the selected persons and no BU node (`GroupActuals.hasTargets` only
 *      looks at the node's selected persons), so a BU without a visible target is UNKNOWN, never "no targets": it is
 *      reported in `busUnknown` and is not a finding. A BU with a visible target is known, so the selected persons of that
 *      BU without a target are listed. Exception: a multi-BU selection that carries a BU node whole (not partial) shows that
 *      BU completely, so without a target anywhere it is "no targets set" after all.
 *   3a. Only active persons put a BU on either list; a BU whose people all left is neither.
 *   4. Group targets count (latent 2027: BAB only on BU / company -> the BU has targets, persons without one are listed).
 * The card and the tab counter both read `targetsFindingCount`.
 */
import type { ActualsResponse, KpiCatalogueEntry, PersonActuals } from '../types/strategicKpis';

export type DataQualityCheck = 'mapping' | 'targets';

/** Bucket for persons without a BU code. */
export const NO_BU = '–';

/** KpiDefinition.TargetGrain bit of PERSON (as in strategicKpiTargetGrid). */
const GRAIN_PERSON = 1;
/** Group lines the engine computes from the others; they carry no stored target of their own. */
const DERIVED_LINES = new Set(['visits.average_pct']);

export interface PersonWithoutTarget {
    staffId: string;
    fullName: string;
    companyCode: string;
    buCode: string;
    teamManagerName: string | null;
}

export interface PersonsWithoutTargets {
    /** Active persons without a target in a BU that is known to have targets, sorted by BU, then name. */
    persons: PersonWithoutTarget[];
    /** BUs known to have no target at all (no selection only) - shown as "No targets set for this BU". */
    busWithoutTargets: string[];
    /** Selection active and the payload shows no target for these BUs: unknown, not a finding. */
    busUnknown: string[];
    /** KPIs that take person targets - the ones the check looked at. */
    personKpiCount: number;
    /** The year has no persons at all (e.g. 2027 before the roster exists): nothing to check. */
    noPersons: boolean;
    /** A selection narrows the response (the engine added a SELECTION node). */
    restricted: boolean;
}

/** KPIs that carry person targets: grain PERSON and not a derived line. */
export function personTargetKpis(data: Pick<ActualsResponse, 'kpis'>): KpiCatalogueEntry[] {
    return data.kpis.filter(k => (k.targetGrain & GRAIN_PERSON) !== 0 && !DERIVED_LINES.has(k.kpiKey));
}

/** True when the person has a stored target on at least one of the given KPIs. */
export function hasSteeredTarget(person: Pick<PersonActuals, 'values'>, kpiKeys: readonly string[]): boolean {
    return kpiKeys.some(k => {
        const t = person.values[k]?.target;
        return t !== null && t !== undefined;
    });
}

export function personsWithoutTargets(data: ActualsResponse): PersonsWithoutTargets {
    const keys = personTargetKpis(data).map(k => k.kpiKey);
    const restricted = data.groups.some(g => g.scopeType === 'SELECTION');
    const buOf = (p: PersonActuals) => p.buCode ?? NO_BU;

    // BUs for which the payload shows a target.
    const known = new Set<string>();
    for (const p of data.persons) if (p.isActive && hasSteeredTarget(p, keys)) known.add(buOf(p));
    for (const g of data.groups) {
        if (g.partial || g.scopeType === 'SELECTION' || g.scopeType === 'TOTAL') continue;
        if (!Object.values(g.values).some(v => v.target !== null && v.target !== undefined)) continue;
        if (g.scopeType === 'TEAM') {
            for (const p of data.persons) if (p.teamManagerStaffId === (g.scopeKey || null)) known.add(buOf(p));
        } else if (g.buCode) known.add(g.buCode);
    }

    const persons: PersonWithoutTarget[] = data.persons
        .filter(p => p.isActive && known.has(buOf(p)) && !hasSteeredTarget(p, keys))
        .map(p => ({
            staffId: p.staffId, fullName: p.fullName, companyCode: p.companyCode,
            buCode: buOf(p), teamManagerName: p.teamManagerName,
        }))
        .sort((a, b) => a.buCode.localeCompare(b.buCode) || a.fullName.localeCompare(b.fullName));
    // Only active persons put a BU on the list: a BU whose people all left has nobody to set a target for.
    const unresolved = [...new Set(data.persons.filter(p => p.isActive).map(buOf))].filter(bu => !known.has(bu)).sort((a, b) => a.localeCompare(b));
    // A BU node that is in the payload whole (not cut by the selection) shows all its persons, so "no target" is certain.
    const whole = new Set(data.groups.filter(g => g.scopeType === 'BU' && !g.partial && g.buCode).map(g => g.buCode as string));
    const certain = (bu: string) => !restricted || whole.has(bu);
    return {
        persons,
        busWithoutTargets: unresolved.filter(certain),
        busUnknown: unresolved.filter(bu => !certain(bu)),
        personKpiCount: keys.length,
        noPersons: data.persons.length === 0,
        restricted,
    };
}

/** The number the card and the tab counter share: persons listed plus BUs known to have no targets. */
export function targetsFindingCount(r: PersonsWithoutTargets): number {
    return r.persons.length + r.busWithoutTargets.length;
}

/** Whether a check has a finding. */
export function checkHasFinding(data: ActualsResponse, check: DataQualityCheck): boolean {
    if (check === 'mapping') return (data.dataQuality?.meetingsWithoutCompanyMapping?.total ?? 0) > 0;
    return targetsFindingCount(personsWithoutTargets(data)) > 0;
}

/** The tab counter: the number of checks with a finding (0, 1 or 2). Null data = 0. */
export function countDataQualityChecks(data: ActualsResponse | null | undefined): number {
    if (!data) return 0;
    return (['mapping', 'targets'] as const).filter(c => checkHasFinding(data, c)).length;
}

/** The engine's window text in English (it arrives as "YTD bis ..."). */
export function dataQualityWindowText(text: string): string {
    return text.replace(/^YTD bis /, 'YTD to ');
}
